import { FormEvent, useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toORPCError } from '@orpc/client'
import { useTranslation } from 'react-i18next'
import { orpc } from '@/lib/orpc-client'
import { PROJECT_ICONS } from '@/lib/project-icons'
import { ICON_MAP } from '@/components/project-icon'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ProjectGroup {
  organization: { id: string; name: string; role: string }
  projects: unknown[]
  projectCount: number
  projectLimit: number
}

interface CreateProjectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectGroups: ProjectGroup[]
  defaultOrganizationId: string
}

export function CreateProjectDialog({
  open,
  onOpenChange,
  projectGroups,
  defaultOrganizationId,
}: CreateProjectDialogProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [selectedOrgId, setSelectedOrgId] = useState(defaultOrganizationId)
  const [name, setName] = useState('')
  const [selectedIcon, setSelectedIcon] = useState<string>('folder')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setSelectedOrgId(defaultOrganizationId)
      setName('')
      setSelectedIcon('folder')
      setError(null)
    }
  }, [open, defaultOrganizationId])

  const selectedGroup = projectGroups.find((g) => g.organization.id === selectedOrgId)
  const atLimit = selectedGroup ? selectedGroup.projectCount >= selectedGroup.projectLimit : true
  const showOrgSelector = projectGroups.length > 1

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (atLimit || !name.trim() || !selectedOrgId) return

    setIsCreating(true)
    setError(null)

    try {
      const project = await orpc.projects.create({
        organizationId: selectedOrgId,
        name: name.trim(),
        icon: selectedIcon || null,
      })

      window.dispatchEvent(new CustomEvent('project-list-changed'))

      // Board 5q82: HUB opens the conversation about the new project. The
      // project exists either way — any failure here lands on the plain chat
      // start page, where the onboarding card offers it again.
      let onboardingThreadId: string | null = null
      try {
        const onboarding = await orpc.chat.startOnboarding({ projectId: project.id })
        onboardingThreadId = onboarding.thread.id
        window.dispatchEvent(new CustomEvent('chat-threads-changed'))
      } catch {
        // Fall through to the chat start page.
      }

      onOpenChange(false)
      if (onboardingThreadId) {
        void navigate({
          to: '/$projectId/chat/$chatId',
          params: { projectId: project.id, chatId: onboardingThreadId },
        })
      } else {
        void navigate({ to: '/$projectId/chat', params: { projectId: project.id } })
      }
    } catch (err) {
      const orpcErr = toORPCError(err)
      setError(orpcErr.message ?? t('createProject.failed'))
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('createProject.title')}</DialogTitle>
          {selectedGroup && (
            <DialogDescription>
              {t('createProject.count', {
                count: selectedGroup.projectCount,
                limit: selectedGroup.projectLimit,
              })}
            </DialogDescription>
          )}
        </DialogHeader>

        {atLimit ? (
          <p className="text-sm text-muted-foreground">
            {t('createProject.limitReached', { limit: selectedGroup?.projectLimit ?? 0 })}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {showOrgSelector && (
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">{t('createProject.organization')}</span>
                <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {projectGroups.map((group) => {
                      const isAtLimit = group.projectCount >= group.projectLimit
                      const canCreate =
                        group.organization.role === 'owner' || group.organization.role === 'admin'
                      return (
                        <SelectItem
                          key={group.organization.id}
                          value={group.organization.id}
                          disabled={isAtLimit || !canCreate}
                        >
                          {group.organization.name} ({group.projectCount}/{group.projectLimit})
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </label>
            )}

            <label className="block space-y-1.5">
              <span className="text-sm font-medium">{t('createProject.name')}</span>
              <input
                type="text"
                required
                maxLength={120}
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
                placeholder={t('createProject.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t('createProject.icon')}</span>
              <div className="flex flex-wrap gap-1.5">
                {PROJECT_ICONS.map((iconKey) => {
                  const Icon = ICON_MAP[iconKey]
                  if (!Icon) return null
                  const isSelected = selectedIcon === iconKey
                  return (
                    <button
                      key={iconKey}
                      type="button"
                      onClick={() => setSelectedIcon(iconKey)}
                      className={`flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-accent ${
                        isSelected
                          ? 'border-primary ring-2 ring-foreground/30 bg-accent'
                          : 'border-transparent'
                      }`}
                      title={iconKey}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  )
                })}
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCreating || !name.trim()}>
                {isCreating ? t('createProject.creating') : t('createProject.submit')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
